/**
 * The toast store: show short confirmations ("PDF saved") from anywhere, including
 * code outside React such as the service worker registration. <ToastViewport />
 * renders them.
 */
export type ToastTone = 'info' | 'success' | 'error'

export interface ToastOptions {
  message: string
  tone?: ToastTone
  /** Milliseconds before it hides itself; 0 keeps it until dismissed. Default 5000. */
  duration?: number
  /** One optional action, such as "Reload". */
  action?: { label: string; onAction: () => void }
}

export interface ToastItem {
  id: number
  message: string
  tone: ToastTone
  action?: ToastOptions['action']
  leaving: boolean
}

/** Matches --motion-fast: toasts fade and rise 8 dp in 150 ms. */
export const TOAST_EXIT_MS = 150

let items: ToastItem[] = []
let nextId = 1
const listeners = new Set<() => void>()
const timers = new Map<number, ReturnType<typeof setTimeout>>()

function update(next: ToastItem[]) {
  items = next
  listeners.forEach((listener) => listener())
}

export const toast = {
  show(options: ToastOptions): number {
    const id = nextId++
    update([...items, { id, message: options.message, tone: options.tone ?? 'info', action: options.action, leaving: false }])
    const duration = options.duration ?? 5000
    if (duration > 0) timers.set(id, setTimeout(() => toast.dismiss(id), duration))
    return id
  },
  dismiss(id: number): void {
    const timer = timers.get(id)
    if (timer) clearTimeout(timer)
    timers.delete(id)
    if (!items.some((item) => item.id === id && !item.leaving)) return
    update(items.map((item) => (item.id === id ? { ...item, leaving: true } : item)))
    setTimeout(() => update(items.filter((item) => item.id !== id)), TOAST_EXIT_MS)
  },
  /** Forget every toast at once (tests, sign-out). */
  clear(): void {
    timers.forEach((timer) => clearTimeout(timer))
    timers.clear()
    update([])
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  getItems(): ToastItem[] {
    return items
  },
}

/** The same store, for components that prefer a hook. */
export function useToast() {
  return toast
}
